import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;
export const BACKEND_BASE = BASE;

const client = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  timeout: 300000,
  withCredentials: true,
});

// Prompt-only interceptor — when the admin is holding Cmd/Ctrl, add
// the header the backend middleware watches for so `send_message()`
// short-circuits after logging. Reading from a module-level flag (set
// by `lib/promptOnly.jsx`) keeps this decoupled from React so it also
// covers non-hook code paths that might call the client directly.
// This tab's property (see lib/tabProperty.js). Sent on every request as
// `X-Property-Id`. The backend scopes editorial endpoints by it — but
// only after checking the account has that property, so it can narrow a
// request, never grant access (backend/property_scope.py). The request
// log also uses it to label rows orphaned before auth resolves.
// Set synchronously by AuthProvider BEFORE the user object reaches React,
// so a page's very first query already carries it.
let scopedPropertyId = "";
export function setScopedPropertyId(id) {
  scopedPropertyId = id || "";
}

client.interceptors.request.use((config) => {
  if (scopedPropertyId) {
    config.headers = config.headers || {};
    config.headers["X-Property-Id"] = scopedPropertyId;
  }
  try {
    // Lazy import so this file has no circular dep with the provider.
    // eslint-disable-next-line global-require
    const { isPromptOnlyHeld } = require("./promptOnly.jsx");
    if (isPromptOnlyHeld()) {
      config.headers = config.headers || {};
      config.headers["X-Prompt-Only"] = "1";
    }
  } catch {
    /* provider not mounted (e.g. login screen) — proceed as normal */
  }
  return config;
});

// Response interceptor — when the backend short-circuits an LLM call
// in prompt-only mode it returns 202 with `{prompt_only: true, ...}`.
// Axios treats 202 as success so the calling code's `.then()` fires
// normally. Callers don't need special handling: we fire a toast HERE
// and then throw a controlled error so the caller's success branch
// doesn't try to render "no output" as if it were a real response.
// Callers that want to opt out (e.g. Prompt History self-inspection)
// can check `err.isPromptOnly` on the rejection.
client.interceptors.response.use(
  (response) => {
    if (response?.data?.prompt_only === true) {
      try {
        // eslint-disable-next-line global-require
        const { toast } = require("sonner");
        toast.success(
          response.data.message ||
            "Prompt saved to Prompt History — no LLM call made.",
          { duration: 5000 },
        );
      } catch {
        /* toast unavailable */
      }
      const err = new Error(
        response.data.message || "Prompt-only mode — no response body",
      );
      err.isPromptOnly = true;
      err.response = response;
      return Promise.reject(err);
    }
    return response;
  },
);

export { client as axiosClient };

// --- Client-side image preflight ------------------------------------------
// Downscale >2000px images to 2000px in the browser BEFORE uploading, so we
// never ship a 24MP camera JPEG across Cloudflare's edge (100MB request cap,
// ~100s stream limit). The backend also resizes, but doing it here avoids
// Cloudflare 520/524 errors and cuts upload time on mobile connections.
const MAX_UPLOAD_WIDTH = 2000;
// Carousel panorama: 7 slides x 1080 px (backend/carousel.py PANORAMA_IMAGE_WIDTH).
const PANORAMA_UPLOAD_WIDTH = 7560;
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024; // 15MB after downscale
// Newsdesk Stock keeps near-original resolution (backend cap: 25 MB).
const STOCK_UPLOAD_WIDTH = 6000;

async function preflightImage(file, { maxWidth = MAX_UPLOAD_WIDTH } = {}) {
  if (!file) throw new Error("No file selected");
  if (!file.type?.startsWith("image/")) {
    throw new Error("Not an image file");
  }
  // Small files pass straight through — no need to re-encode.
  if (file.size < 800 * 1024) return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    // Some browsers can't decode HEIC/RAW via createImageBitmap; hand off
    // to the backend and let it try (Pillow handles more formats).
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new Error(
        `Image is ${(file.size / 1024 / 1024).toFixed(1)}MB. ` +
          "Please pick something under 15MB or export a smaller JPEG."
      );
    }
    return file;
  }

  if (bitmap.width <= maxWidth && file.size < MAX_UPLOAD_BYTES) {
    bitmap.close?.();
    return file;
  }

  const scale = Math.min(1, maxWidth / bitmap.width);
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Canvas encoding failed"))),
      "image/jpeg",
      0.9,
    );
  });
  if (blob.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `Image is still ${(blob.size / 1024 / 1024).toFixed(1)}MB after downscale. Try a smaller source.`,
    );
  }
  const outName = (file.name || "image")
    .replace(/\.[^.]+$/, "")
    .concat(".jpg");
  return new File([blob], outName, { type: "image/jpeg" });
}

export const api = {
  // ---- auth ----
  authMe: () => client.get("/auth/me").then((r) => r.data),
  authLogin: (email, password) =>
    client.post("/auth/login", { email, password }).then((r) => r.data),
  authVerify2fa: (code) =>
    client.post("/auth/verify-2fa", { code }).then((r) => r.data),
  authLogout: () => client.post("/auth/logout").then((r) => r.data),
  authSwitchProperty: (propertyId) =>
    client.post("/auth/switch-property", { property_id: propertyId }).then((r) => r.data),

  // ---- admin (admin role only) ----
  adminListProperties: () =>
    client.get("/admin/properties").then((r) => r.data),
  adminCreateProperty: (payload) =>
    client.post("/admin/properties", payload).then((r) => r.data),
  adminUpdateProperty: (id, payload) =>
    client.patch(`/admin/properties/${id}`, payload).then((r) => r.data),
  adminDeleteProperty: (id) =>
    client.delete(`/admin/properties/${id}`).then((r) => r.data),
  // PublishOS link (billing): pair a property + choose the 3 products.
  adminPublishosProperties: () =>
    client.get("/admin/publishos/properties").then((r) => r.data),
  adminPublishosProducts: (publishosPropertyId) =>
    client
      .get("/admin/publishos/products", {
        params: { publishos_property_id: publishosPropertyId },
      })
      .then((r) => r.data),
  adminSetPublishosPairing: (id, payload) =>
    client.put(`/admin/properties/${id}/publishos`, payload).then((r) => r.data),
  adminListUsers: () => client.get("/admin/users").then((r) => r.data),
  adminCreateUser: (payload) =>
    client.post("/admin/users", payload).then((r) => r.data),
  adminUpdateUser: (id, payload) =>
    client.patch(`/admin/users/${id}`, payload).then((r) => r.data),
  adminDeleteUser: (id) => client.delete(`/admin/users/${id}`).then((r) => r.data),
  adminGetLlmModels: () => client.get("/admin/llm-models").then((r) => r.data),
  adminPutLlmModels: (tasks) =>
    client.put("/admin/llm-models", { tasks }).then((r) => r.data),
  adminListRequestLog: (params = {}) =>
    client.get("/admin/request-log", { params }).then((r) => r.data),
  adminDeleteRequestLogEntry: (id) =>
    client.delete(`/admin/request-log/${id}`).then((r) => r.data),
  adminClearRequestLog: (propertyId = null) =>
    client
      .delete("/admin/request-log", {
        params: propertyId ? { property_id: propertyId } : {},
      })
      .then((r) => r.data),
  adminGetLlmCost: (window = "30d", propertyId = null) =>
    client
      .get("/admin/llm-cost", {
        params: propertyId ? { window, property_id: propertyId } : { window },
      })
      .then((r) => r.data),
  auth2faSetup: () => client.post("/auth/2fa/setup").then((r) => r.data),
  auth2faEnable: (code) =>
    client.post("/auth/2fa/enable", { code }).then((r) => r.data),
  auth2faDisable: (password) =>
    client.post("/auth/2fa/disable", { password }).then((r) => r.data),

  listWriters: () => client.get("/writers").then((r) => r.data),

  createResearch: (payload) =>
    client.post("/research", payload).then((r) => r.data),
  listResearch: () => client.get("/research").then((r) => r.data),
  getResearch: (id) => client.get(`/research/${id}`).then((r) => r.data),
  getResearchSourceReport: (id) =>
    client.get(`/research/${id}/source-report`).then((r) => r.data),
  deleteResearch: (id) =>
    client.delete(`/research/${id}`).then((r) => r.data),

  // Recurring research schedules (day-of-week + local time).
  // `days_of_week` is 0 = Monday ... 6 = Sunday to match the backend's
  // datetime.weekday(). Do NOT feed it JS getDay(), which is Sun-first.
  listResearchSchedules: () =>
    client.get("/research-schedules").then((r) => r.data),
  createResearchSchedule: (payload) =>
    client.post("/research-schedules", payload).then((r) => r.data),
  updateResearchSchedule: (id, patch) =>
    client.patch(`/research-schedules/${id}`, patch).then((r) => r.data),
  deleteResearchSchedule: (id) =>
    client.delete(`/research-schedules/${id}`).then((r) => r.data),
  runResearchScheduleNow: (id) =>
    client.post(`/research-schedules/${id}/run-now`).then((r) => r.data),

  // Print Edition workflow
  createPrintJob: (payload) =>
    client.post("/print-edition/jobs", payload).then((r) => r.data),
  getPrintJob: (id) =>
    client.get(`/print-edition/jobs/${id}`).then((r) => r.data),
  listPrintJobs: () =>
    client.get("/print-edition/jobs").then((r) => r.data),
  deletePrintJob: (id) =>
    client.delete(`/print-edition/jobs/${id}`).then((r) => r.data),
  // { usage: {article_id: {status, print_count, last_printed_at,
  //   in_progress}}, cleared_at: {article_id: iso} }
  getPrintUsage: () =>
    client.get("/print-edition/usage").then((r) => ({
      usage: r.data?.usage || {},
      cleared_at: r.data?.cleared_at || {},
    })),
  // Unmark (cleared=true) or restore (cleared=false) articles as
  // printed — for leftovers that didn't make the paper. Never edits
  // the export history.
  setPrintUsageCleared: (articleIds, cleared) =>
    client
      .post("/print-edition/usage/clear", {
        article_ids: articleIds,
        cleared,
      })
      .then((r) => r.data),
  // Plain navigation (window.location / <a href>) can't carry the
  // X-Property-Id header, so the tab's property rides in the query string.
  printJobDownloadUrl: (id) => {
    const url = `/api/print-edition/jobs/${id}/download`;
    return scopedPropertyId
      ? `${url}?scope_property_id=${encodeURIComponent(scopedPropertyId)}`
      : url;
  },
  // Images of the selected articles as one streamed .zip. Plain
  // navigation for the same reason as above; ids keep selection order
  // so the numbering matches the .docx.
  printImagesZipUrl: (ids) => {
    const qs = new URLSearchParams({ ids: ids.join(",") });
    if (scopedPropertyId) qs.set("scope_property_id", scopedPropertyId);
    return `/api/print-edition/images.zip?${qs.toString()}`;
  },

  generateArticle: (payload) =>
    client.post("/articles/generate", payload).then((r) => r.data),
  getGenerationTask: (taskId) =>
    client.get(`/articles/generate/${taskId}`).then((r) => r.data),

  // Shared background-task polling used by all three article-generation
  // flows (idea → article, custom, telegram tip → article). Each caller
  // supplies its own POST kickoff — this helper polls the shared
  // `article_generation_tasks` collection until status is ready/error and
  // then fetches the finished article.
  _pollArticleTask: async (taskId, { onTick } = {}) => {
    const start = Date.now();
    // Article generation can retry up to 3× on empty body_html, so the
    // ceiling is roughly 3 × Anthropic-round-trip. 10 min is a generous
    // envelope; the actual happy path finishes in 30–60 s.
    while (Date.now() - start < 10 * 60 * 1000) {
      await new Promise((r) => setTimeout(r, 3000));
      const task = await client
        .get(`/articles/generate/${taskId}`)
        .then((r) => r.data);
      if (typeof onTick === "function") onTick(task);
      if (task.status === "ready") {
        return await client
          .get(`/articles/${task.article_id}`)
          .then((r) => r.data);
      }
      // Admin held Cmd/Ctrl → the background LLM call short-circuited
      // after logging its prompt. Surface as a success toast + a
      // controlled rejection so the caller's ".then" success path
      // doesn't try to render a non-existent article. Mirrors the
      // axios-response interceptor's handling for direct 202s.
      if (task.status === "prompt_only") {
        try {
          // eslint-disable-next-line global-require
          const { toast } = require("sonner");
          toast.success(
            task.message ||
              "Prompt saved to Prompt History — no LLM call made.",
            { duration: 5000 },
          );
        } catch {
          /* toast unavailable */
        }
        const err = new Error(
          task.message || "Prompt-only mode — no article generated",
        );
        err.isPromptOnly = true;
        throw err;
      }
      if (task.status === "error") {
        const err = new Error(task.error || "Article generation failed");
        err.taskError = true;
        throw err;
      }
    }
    throw new Error("Article generation timed out after 10 minutes");
  },

  // Kicks off article generation as a background task and polls until it
  // finishes. Long-running LLM work would otherwise exceed Cloudflare's
  // 100s HTTP timeout on production.
  generateArticleAndWait: async function (payload, { onTick } = {}) {
    const { task_id } = await client
      .post("/articles/generate", payload)
      .then((r) => r.data);
    return await this._pollArticleTask(task_id, { onTick });
  },

  // Custom-article generation — same 202 + polling pattern as the idea
  // path (was previously synchronous which caused Cloudflare 524s on
  // long generations).
  createCustomArticleAndWait: async function (payload, { onTick } = {}) {
    const { task_id } = await client
      .post("/custom-articles/generate", payload)
      .then((r) => r.data);
    return await this._pollArticleTask(task_id, { onTick });
  },

  // Telegram tip → article. Returns the fully hydrated article once
  // status becomes `ready`.
  generateFromTipAndWait: async function (tipId, payload, { onTick } = {}) {
    const { task_id } = await client
      .post(`/telegram/tips/${tipId}/generate`, payload)
      .then((r) => r.data);
    return await this._pollArticleTask(task_id, { onTick });
  },

  listArticles: () => client.get("/articles").then((r) => r.data),
  getArticle: (id) => client.get(`/articles/${id}`).then((r) => r.data),
  updateArticle: (id, patch) =>
    client.patch(`/articles/${id}`, patch).then((r) => r.data),
  deleteArticle: (id) => client.delete(`/articles/${id}`).then((r) => r.data),
  listTrashedArticles: () =>
    client.get("/articles-trash").then((r) => r.data),
  restoreArticle: (id) =>
    client.post(`/articles/${id}/restore`).then((r) => r.data),
  purgeArticle: (id) =>
    client.delete(`/articles/${id}/purge`).then((r) => r.data),
  reviseArticle: (id, prompt) =>
    client.post(`/articles/${id}/revise`, { prompt }).then((r) => r.data),
  articleSourceNote: (id, action) =>
    client.post(`/articles/${id}/source-note`, { action }).then((r) => r.data),
  previewSourceNote: (template) =>
    client.post("/settings/source-note/preview", { template })
      .then((r) => r.data),
  // Disclosure legend preview: the UNSAVED style rendered by the same
  // Pillow path as publishing. Returns a JPEG Blob. `file` (optional)
  // is a test photo; `sample` = "article" | "light" | "dark".
  previewImageLegend: ({ style, text = "", sample = "article", file = null, signal } = {}) => {
    const fd = new FormData();
    fd.append("style", JSON.stringify(style || {}));
    fd.append("text", text);
    fd.append("sample", sample);
    if (file) fd.append("file", file);
    return client
      .post("/settings/image-legend/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
        timeout: 60000,
        signal,
      })
      .then((r) => r.data);
  },
  restoreArticleVersion: (id, version) =>
    client.post(`/articles/${id}/restore-version`, { version }).then((r) => r.data),
  uploadArticle: (payload) =>
    client.post("/articles/upload", payload).then((r) => r.data),
  recommendUploadCategories: (payload) =>
    client
      .post("/articles/upload/recommend-categories", payload)
      .then((r) => r.data),
  // -- internal-link index --
  wpArticlesStatus: () =>
    client.get("/wordpress/articles/status").then((r) => r.data),
  // WordPress sync runs as a background task on the backend to survive
  // Cloudflare's 100s HTTP timeout. Poll /wordpress/sync-tasks/{id}.
  _pollSyncTask: async (taskId) => {
    const started = Date.now();
    while (Date.now() - started < 5 * 60 * 1000) {
      await new Promise((r) => setTimeout(r, 3000));
      const task = await client
        .get(`/wordpress/sync-tasks/${taskId}`)
        .then((r) => r.data);
      if (task.status === "ready") return task.result;
      if (task.status === "error") {
        const err = new Error(task.error || "WordPress sync failed");
        err.taskError = true;
        throw err;
      }
    }
    throw new Error("WordPress sync timed out after 5 minutes");
  },
  syncWpArticles: async (incremental = false) => {
    const { task_id } = await client
      .post(`/wordpress/sync-articles?incremental=${incremental}`, {}, {
        timeout: 30000,
      })
      .then((r) => r.data);
    return await api._pollSyncTask(task_id);
  },
  wpArticlesSearch: (q, limit = 15) =>
    client
      .get("/wordpress/articles/search", { params: { q, limit } })
      .then((r) => r.data),
  // -- custom articles --
  customArticleUploadImage: async (file) => {
    const processed = await preflightImage(file);
    const fd = new FormData();
    fd.append("file", processed);
    return client
      .post("/custom-articles/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
      .then((r) => r.data);
  },
  customArticleExtractFile: (file) => {
    // Press release / transcript / briefing document → server-side
    // text extraction (with Gemini OCR fallback for scanned PDFs).
    // Larger timeout because OCR of a multi-page scanned PDF can
    // take 30–60s on the LLM side.
    const fd = new FormData();
    fd.append("file", file);
    return client
      .post("/custom-articles/extract-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      })
      .then((r) => r.data);
  },
  customArticleFindMoreSources: (payload) =>
    client
      .post("/custom-articles/find-more-sources", payload, { timeout: 120000 })
      .then((r) => r.data),
  createCustomArticle: (payload) =>
    client
      .post("/custom-articles", payload, { timeout: 300000 })
      .then((r) => r.data),
  // -- ideas search --
  searchIdeas: (q, limit = 80) =>
    client.get("/ideas/search", { params: { q, limit } }).then((r) => r.data),
  uploadArticleImage: async (id, file) => {
    const processed = await preflightImage(file);
    const fd = new FormData();
    fd.append("file", processed);
    return client
      .post(`/articles/${id}/upload-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
      .then((r) => r.data);
  },
  // Server-side fetch of a source-article image URL → featured image.
  // Saves editors the download-and-reupload dance for press-room photos.
  importSourceImage: (id, url) =>
    client.post(`/articles/${id}/import-source-image`, { url }).then((r) => r.data),

  // -- Stock photos (Photo recommendations thumbnails) --
  // `q` omitted → one group per LLM photo suggestion; `q` set → one
  // group for that editor query. `provider` omitted → backend default.
  getStockPhotos: (id, { q, provider } = {}) =>
    client
      .get(`/articles/${id}/stock-photos`, {
        params: { ...(q ? { q } : {}), ...(provider ? { provider } : {}) },
      })
      .then((r) => r.data),
  importStockPhoto: (id, { provider, url, credit, download_location, stock_id }) =>
    client
      .post(`/articles/${id}/import-stock-photo`, {
        provider,
        url: url || "",
        credit,
        download_location: download_location || "",
        stock_id: stock_id || "",
      })
      .then((r) => r.data),
  // Body-image modal → Stock tab (Pexels / Unsplash / Newsdesk Stock).
  addBodyStockImage: (articleId, photo, paragraphIndex) =>
    client
      .post(`/articles/${articleId}/body-images/stock`, {
        provider: photo.provider,
        url: photo.download_url || "",
        credit: photo.credit || "",
        download_location: photo.download_location || "",
        stock_id: photo.stock_id || "",
        alt: photo.alt || "",
        paragraph_index: paragraphIndex,
      })
      .then((r) => r.data),

  // -- Newsdesk Stock (own photo library in Cloudflare R2) --
  stockLibraryStatus: () =>
    client.get("/stock-library/status").then((r) => r.data),
  listStockLibrary: ({ q, status = "active", sort = "newest", limit = 60, offset = 0 } = {}) =>
    client
      .get("/stock-library", {
        params: { ...(q ? { q } : {}), status, sort, limit, offset },
      })
      .then((r) => r.data),
  getStockLibraryPhoto: (id) =>
    client.get(`/stock-library/${id}`).then((r) => r.data),
  // `fields`: keywords, credit, title, alt — plus pending_article_id /
  // pending_image_url / pending_kind when called from the article
  // page's "Add this photo to Newsdesk Stock" checkbox. Library copies
  // keep far more resolution than article uploads (6000px vs 2000px):
  // they're the archive, and print uses them.
  uploadStockLibraryPhoto: async (file, fields = {}) => {
    const processed = await preflightImage(file, { maxWidth: STOCK_UPLOAD_WIDTH });
    const fd = new FormData();
    fd.append("file", processed);
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== undefined && v !== null) fd.append(k, String(v));
    });
    return client
      .post("/stock-library", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      })
      .then((r) => r.data);
  },
  patchStockLibraryPhoto: (id, patch) =>
    client.patch(`/stock-library/${id}`, patch).then((r) => r.data),
  replaceStockLibraryFile: async (id, file) => {
    const processed = await preflightImage(file, { maxWidth: STOCK_UPLOAD_WIDTH });
    const fd = new FormData();
    fd.append("file", processed);
    return client
      .post(`/stock-library/${id}/version`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      })
      .then((r) => r.data);
  },
  deleteStockLibraryPhoto: (id) =>
    client.delete(`/stock-library/${id}`).then((r) => r.data),
  // Save one of an article's scraped source images to the library
  // (live immediately). Returns the photo + `duplicate`.
  addSourceImageToStock: (articleId, url, fields = {}) =>
    client
      .post("/stock-library/from-source", { article_id: articleId, url, ...fields }, { timeout: 120000 })
      .then((r) => r.data),
  // {saved: {sourceUrl: stockId}} for the "In Stock" marker on source tiles.
  stockSourceStatus: (articleId) =>
    client.get(`/stock-library/source-status/${articleId}`).then((r) => r.data),
  // "Add to Stock" for an image already on the article (featured or
  // body) — live at once. Activates a pending copy when there is one.
  addArticleImageToStock: (articleId, imageUrl, fields = {}) =>
    client
      .post(
        "/stock-library/from-article",
        { article_id: articleId, image_url: imageUrl, ...fields },
        { timeout: 120000 },
      )
      .then((r) => r.data),
  // {saved: {imageUrl: {id, status}}} for the article's own images.
  stockArticleStatus: (articleId) =>
    client.get(`/stock-library/article-status/${articleId}`).then((r) => r.data),


  // -- Body images (inline images placed between paragraphs) --
  // All three ways to create a body image return the full updated
  // article so the caller can invalidate a single query key.
  uploadBodyImage: async (articleId, file, paragraphIndex) => {
    // Same browser-side downscale the featured-image upload does. It
    // was missing here, so a 24MP camera JPEG (or a Retina screenshot
    // pasted from the clipboard) went across Cloudflare at full size.
    const processed = await preflightImage(file);
    const fd = new FormData();
    fd.append("file", processed);
    fd.append("paragraph_index", String(paragraphIndex));
    return client.post(`/articles/${articleId}/body-images/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    }).then((r) => r.data);
  },
  importBodyImage: (articleId, url, paragraphIndex) =>
    client.post(`/articles/${articleId}/body-images/import`, {
      url, paragraph_index: paragraphIndex,
    }).then((r) => r.data),
  generateBodyImage: (articleId, paragraphIndex, subject) =>
    client.post(`/articles/${articleId}/body-images/generate`, {
      paragraph_index: paragraphIndex, subject: subject || undefined,
    }, { timeout: 180000 }).then((r) => r.data),
  patchBodyImage: (articleId, imgId, patch) =>
    client.patch(`/articles/${articleId}/body-images/${imgId}`, patch).then((r) => r.data),
  deleteBodyImage: (articleId, imgId) =>
    client.delete(`/articles/${articleId}/body-images/${imgId}`).then((r) => r.data),
  // -- Digital editions (backend/routes/editions.py). Publishing, the
  // cover image and deleting use the article routes above/below.
  listEditions: () => client.get("/editions").then((r) => r.data),
  suggestEdition: () => client.get("/editions/suggest").then((r) => r.data),
  createEdition: (payload) =>
    client.post("/editions", payload).then((r) => r.data),
  updateEdition: (id, payload) =>
    client.patch(`/editions/${id}`, payload).then((r) => r.data),
  publishArticle: (id, status, scheduledDate) =>
    client
      .post(`/articles/${id}/publish`, {
        article_id: id,
        status,
        ...(scheduledDate ? { scheduled_date: scheduledDate } : {}),
      })
      .then((r) => r.data),

  generateArticleImage: (id, payload = {}) =>
    client
      .post(`/articles/${id}/generate-image`, payload)
      .then((r) => r.data),

  // Horizontalize: landscape canvas, blurred fill, the photo on top.
  horizontalizeArticleImage: (id, payload) =>
    client
      .post(`/articles/${id}/horizontalize-image`, payload, { timeout: 120000 })
      .then((r) => r.data),
  revertHorizontalizedImage: (id) =>
    client
      .post(`/articles/${id}/horizontalize-image/revert`)
      .then((r) => r.data),

  runArticleFactCheck: (id) =>
    client.post(`/articles/${id}/fact-check`).then((r) => r.data),
  // Accept the fix the fact checker proposed for one claim
  // (backend/fact_check_fixes.py). Returns the updated article.
  applyFactCheckFix: (id, checkId, claimIndex) =>
    client
      .post(`/articles/${id}/fact-checks/${checkId}/claims/${claimIndex}/apply`)
      .then((r) => r.data),
  // Undo an accepted fix — puts the original passage back. Returns the
  // updated article.
  undoFactCheckFix: (id, checkId, claimIndex) =>
    client
      .post(`/articles/${id}/fact-checks/${checkId}/claims/${claimIndex}/undo`)
      .then((r) => r.data),

  uploadJsonArticle: (jsonText, writerKey) =>
    client
      .post(`/admin/upload-json-article`, {
        json_text: jsonText,
        writer_key: writerKey,
      })
      .then((r) => r.data),

  // --- Sponsor Ads (per-property CRUD) ---
  listAds: () => client.get(`/ads`).then((r) => r.data.ads || []),
  createAd: (payload) => client.post(`/ads`, payload).then((r) => r.data),
  patchAd: (id, payload) => client.patch(`/ads/${id}`, payload).then((r) => r.data),
  deleteAd: (id) => client.delete(`/ads/${id}`).then((r) => r.data),
  uploadAdLogo: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/ads/upload-logo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  // Preview endpoint returns raw JPEG bytes — the caller wraps them in
  // an object URL for <img src>. Using axios with responseType=blob so
  // the interceptors don't try to JSON-parse the response.
  fetchFacebookAdPreview: (articleId, payload) =>
    client
      .post(`/articles/${articleId}/facebook-ad-preview`, payload, {
        responseType: "blob",
      })
      .then((r) => r.data),

  // Sponsor Ads analytics — engagement counts per placement.
  // `range` (optional): { since: 'YYYY-MM-DD', until: 'YYYY-MM-DD' }
  // filters placements by publish date. When passed, `refresh=true`
  // only re-fetches Meta for the filtered subset (per user spec).
  getAdAnalytics: (adId, refresh = false, range = null) =>
    client
      .get(`/ads/${adId}/analytics`, {
        params: {
          refresh,
          ...(range?.since ? { since: range.since } : {}),
          ...(range?.until ? { until: range.until } : {}),
        },
      })
      .then((r) => r.data),
  deleteAdPlacement: (adId, placementId) =>
    client
      .delete(`/ads/${adId}/placements/${placementId}`)
      .then((r) => r.data),

  // Diagnose a single placement's live Meta response — used by the
  // per-row 🩺 button. Reveals whether Meta is dropping fields (missing
  // `read_insights` permission, wrong post type, expired token, ...).
  diagnosePlacement: (placementId) =>
    client
      .get(`/ads/placements/${placementId}/diagnose`)
      .then((r) => r.data),

  // Client-facing share links for an ad's analytics. `create` returns
  // the plain token ONCE (the backend only stores its hash) — the UI
  // builds the URL from window.location.origin and shows it with a
  // copy button; after that the token is unrecoverable by design.
  listAdShareLinks: (adId) =>
    client.get(`/ads/${adId}/share-links`).then((r) => r.data.links || []),
  createAdShareLink: (adId, payload) =>
    client.post(`/ads/${adId}/share-links`, payload).then((r) => r.data),
  revokeAdShareLink: (adId, linkId) =>
    client
      .post(`/ads/${adId}/share-links/${linkId}/revoke`)
      .then((r) => r.data),

  // Pre-publication story review links (public `/review/{token}` page)
  listReviewLinks: (articleId) =>
    client
      .get(`/articles/${articleId}/review-links`)
      .then((r) => r.data.links || []),
  createReviewLink: (articleId, payload) =>
    client
      .post(`/articles/${articleId}/review-links`, payload)
      .then((r) => r.data),
  revokeReviewLink: (articleId, linkId) =>
    client
      .post(`/articles/${articleId}/review-links/${linkId}/revoke`)
      .then((r) => r.data),
  listReviewComments: (articleId) =>
    client
      .get(`/articles/${articleId}/review-comments`)
      .then((r) => r.data),
  resolveReviewComment: (articleId, commentId, resolved) =>
    client
      .post(`/articles/${articleId}/review-comments/${commentId}/resolve`, {
        resolved: !!resolved,
      })
      .then((r) => r.data),
  deleteReviewComment: (articleId, commentId) =>
    client
      .delete(`/articles/${articleId}/review-comments/${commentId}`)
      .then((r) => r.data),

  // Nightly 03:00 stats refresh (placements + newsletter engagement).
  getStatsRefreshStatus: () =>
    client.get(`/stats-refresh/status`).then((r) => r.data),
  runStatsRefreshNow: () =>
    client.post(`/stats-refresh/run`).then((r) => r.data),

  // Nightly per-property backups to the private R2 bucket.
  getBackupStatus: () => client.get(`/backups/status`).then((r) => r.data),
  runBackupNow: () => client.post(`/backups/run`).then((r) => r.data),
  saveBackupSettings: (patch) =>
    client.put(`/backups/settings`, patch).then((r) => r.data),
  // Restore (Admin page) — needs no property.
  adminBackupBucket: () =>
    client.get(`/admin/backups/bucket`).then((r) => r.data),
  adminBackupSnapshots: (scope) =>
    client
      .get(`/admin/backups/snapshots`, { params: { scope } })
      .then((r) => r.data),
  adminBackupJobs: () => client.get(`/admin/backups/jobs`).then((r) => r.data),
  adminStartBackupJob: (payload) =>
    client.post(`/admin/backups/jobs`, payload).then((r) => r.data),

  // ---- Newsletter ----
  listNewsletterAds: () =>
    client.get("/newsletter-ads").then((r) => r.data),
  createNewsletterAd: (payload) =>
    client.post("/newsletter-ads", payload).then((r) => r.data),
  patchNewsletterAd: (id, payload) =>
    client.patch(`/newsletter-ads/${id}`, payload).then((r) => r.data),
  deleteNewsletterAd: (id) =>
    client.delete(`/newsletter-ads/${id}`).then((r) => r.data),
  newsletterCandidates: (params = {}) =>
    client
      .get("/newsletter/candidates", { params })
      .then((r) => r.data),
  refreshNewsletterEngagement: (articleIds, force = false) =>
    client
      .post("/newsletter/engagement/refresh", {
        article_ids: articleIds,
        force,
      })
      .then((r) => r.data),
  generateTeaser: (articleId) =>
    client
      .post("/newsletter/teaser", { article_id: articleId })
      .then((r) => r.data),
  newsletterGroups: () =>
    client.get("/newsletter/groups").then((r) => r.data),
  // Campaign languages — drives the unsubscribe-page language picker.
  newsletterLanguages: () =>
    client.get("/newsletter/languages").then((r) => r.data),
  // One newsletter's contents (articles + advertisers, no artwork).
  newsletterDetail: (id) =>
    client.get(`/newsletters/${id}`).then((r) => r.data),
  // PublishOS link: is it on for this property, client search, and
  // report / retry one newsletter's ads (with any decisions).
  publishosStatus: () => client.get("/publishos/status").then((r) => r.data),
  publishosClients: (q) =>
    client.get("/publishos/clients", { params: { q } }).then((r) => r.data),
  reportNewsletterToPublishOS: (id, decisions = {}) =>
    client.post(`/newsletters/${id}/publishos`, { decisions }).then((r) => r.data),
  newsletterPreview: (payload) =>
    client.post("/newsletter/preview", payload).then((r) => r.data),
  newsletterThemePreview: (payload) =>
    client.post("/newsletter/theme-preview", payload).then((r) => r.data),
  createNewsletterDraft: (payload) =>
    client.post("/newsletter/draft", payload).then((r) => r.data),
  composeNewsletter: (payload) =>
    client.post("/newsletter/compose", payload).then((r) => r.data),
  // "Add from link": a post on this property's own WordPress site →
  // a newsletter entry snapshot (backend/newsletter_links.py).
  newsletterLinkEntry: (url) =>
    client.post("/newsletter/link-entry", { url }).then((r) => r.data),
  // Unmark (cleared=true) or restore (cleared=false) one article in
  // the newsletter picker. Never edits newsletter history.
  setNewsletterUsageCleared: (articleId, cleared) =>
    client
      .post("/newsletter/usage/clear", {
        article_id: articleId,
        cleared,
      })
      .then((r) => r.data),

  sendNewsletter: (payload) =>
    client.post("/newsletter/send", payload).then((r) => r.data),
  newsletterPromptDefaults: () =>
    client.get("/newsletter/prompt-defaults").then((r) => r.data),
  listNewsletters: () =>
    client.get("/newsletters").then((r) => r.data),
  // `scheduled_local` is "YYYY-MM-DDTHH:MM" wall-clock in the property's
  // newsroom timezone — the server converts it, not the browser.
  scheduleNewsletter: (payload) =>
    client.post("/newsletter/schedule", payload).then((r) => r.data),
  // mode: "everywhere" (also MailerLite) | "newsdesk_only"
  deleteNewsletter: (id, mode) =>
    client
      .delete(`/newsletters/${id}`, { params: { mode } })
      .then((r) => r.data),

  // Admin-only aggregated health snapshot for the sidebar widget.
  // Server returns 401 for non-admins; the widget hides itself in that
  // case so no extra client-side gating is required beyond the API.
  healthDetail: () =>
    client.get("/health/detail").then((r) => r.data),

  // Sponsor Ad style layout editor — per-style saved layouts.
  getAdStyleDefault: (styleKey) =>
    client.get(`/ads/style-defaults/${styleKey}`).then((r) => r.data.layout),
  patchAdStyle: (adId, styleKey, layout) =>
    client.patch(`/ads/${adId}/styles/${styleKey}`, layout).then((r) => r.data),
  // `signal` (an AbortController's) lets the editor drop a preview that
  // has been overtaken by a newer change instead of waiting for it.
  previewAdStyle: (adId, styleKey, layout, signal) => {
    return client
      .post(`/ads/${adId}/preview`, layout, {
        params: { style: styleKey },
        responseType: "blob",
        signal,
      })
      .then((r) => r.data);
  },

  // -- sponsor banner builder (1300x200) --
  // `fields`: { brief, adId, useClientLogo, photos: File[], logo: File,
  // finishedAd: File }. Returns { spec, sources, model }.
  generateAdBanner: (fields) => {
    const fd = new FormData();
    fd.append("brief", fields.brief || "");
    fd.append("ad_id", fields.adId || "");
    fd.append("use_client_logo", fields.useClientLogo ? "true" : "false");
    (fields.photos || []).forEach((f) => fd.append("photos", f));
    if (fields.logo) fd.append("logo", fields.logo);
    if (fields.finishedAd) fd.append("finished_ad", fields.finishedAd);
    return client.post(`/ads/banner/generate`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  // Draw the banner from a spec — no AI call. Returns a PNG blob.
  renderAdBanner: (spec, sources, signal) =>
    client
      .post(`/ads/banner/render`, { spec, sources, save: false }, {
        responseType: "blob",
        signal,
      })
      .then((r) => r.data),
  // Same drawing, stored. Returns { path, spec, width, height }.
  saveAdBanner: (spec, sources) =>
    client
      .post(`/ads/banner/render`, { spec, sources, save: true })
      .then((r) => r.data),

  // -- style asset uploads (sponsor bar PNG, custom images) --
  uploadAdAsset: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/ads/upload-asset`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },

  // -- fonts: bundled + Google Fonts installer --
  listFonts: () => client.get(`/ads/fonts`).then((r) => r.data.fonts || []),
  installFont: (family, weights) =>
    client.post(`/ads/fonts/install`, { family, weights }).then((r) => r.data),
  deleteFont: (family) =>
    client.delete(`/ads/fonts/${encodeURIComponent(family)}`).then((r) => r.data),

  // Sponsor caption snippets — per-property pool of "brought to you by …"
  // footers appended to FB posts that include an ad. See
  // /app/backend/sponsor_snippets.py.
  listSponsorSnippets: () =>
    client.get(`/ads/sponsor-snippets`).then((r) => r.data),
  createSponsorSnippet: (payload) =>
    client.post(`/ads/sponsor-snippets`, payload).then((r) => r.data),
  patchSponsorSnippet: (id, payload) =>
    client.patch(`/ads/sponsor-snippets/${id}`, payload).then((r) => r.data),
  deleteSponsorSnippet: (id) =>
    client.delete(`/ads/sponsor-snippets/${id}`).then((r) => r.data),

  getArticleImagePrompt: (id) =>
    client.get(`/articles/${id}/image-prompt`).then((r) => r.data),

  // -- story-card assets --
  uploadCardLogo: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client
      .post("/cards/upload-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  uploadCardBackground: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client
      .post("/cards/upload-background", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  getSettings: () => client.get("/settings").then((r) => r.data),
  updateSettings: (patch) =>
    client.patch("/settings", patch).then((r) => r.data),
  // Settings → Prompts backup. Import and reset take `dryRun`: true
  // returns the list of changes without saving anything.
  exportPrompts: () =>
    client.get("/settings/prompts/export").then((r) => r.data),
  importPrompts: (bundle, dryRun = true) =>
    client
      .post("/settings/prompts/import", { bundle, dry_run: dryRun })
      .then((r) => r.data),
  resetPrompts: (dryRun = true) =>
    client
      .post("/settings/prompts/reset", { dry_run: dryRun })
      .then((r) => r.data),

  syncWordpress: async () => {
    const { task_id } = await client
      .post("/wordpress/sync", {}, { timeout: 30000 })
      .then((r) => r.data);
    return await api._pollSyncTask(task_id);
  },

  probeSource: (payload) =>
    client.post("/sources/probe", payload).then((r) => r.data),

  findMoreSources: (ideaId) =>
    client
      .post(`/ideas/${ideaId}/find-more-sources`)
      .then((r) => r.data),

  addSourcesToIdea: (ideaId, urls) =>
    client
      .post(`/ideas/${ideaId}/add-sources`, { urls })
      .then((r) => r.data),

  // -- telegram tips --
  telegramStatus: () => client.get("/telegram/status").then((r) => r.data),
  telegramSetup: (botToken) =>
    client
      .post("/telegram/setup", { bot_token: botToken })
      .then((r) => r.data),
  telegramDisable: () => client.delete("/telegram/setup").then((r) => r.data),
  listTips: (status) =>
    client
      .get("/telegram/tips", status ? { params: { status } } : {})
      .then((r) => r.data),
  getTip: (id) => client.get(`/telegram/tips/${id}`).then((r) => r.data),
  createManualTip: (rawText) =>
    client
      .post("/telegram/tips/manual", { raw_text: rawText })
      .then((r) => r.data),
  generateFromTip: (id, payload) =>
    client
      .post(`/telegram/tips/${id}/generate`, payload, { timeout: 300000 })
      .then((r) => r.data),
  rejectTip: (id) =>
    client.post(`/telegram/tips/${id}/reject`).then((r) => r.data),
  deleteTip: (id) => client.delete(`/telegram/tips/${id}`).then((r) => r.data),
  retryTip: (id) =>
    client.post(`/telegram/tips/${id}/retry`).then((r) => r.data),

  // Prompt history — the log of every prompt we've sent to the LLM,
  // scoped to the caller's active property.
  listPrompts: (params = {}) =>
    client.get("/prompts", { params }).then((r) => r.data),
  deletePrompt: (id) => client.delete(`/prompts/${id}`).then((r) => r.data),
  clearPrompts: () => client.delete("/prompts").then((r) => r.data),

  // WordPress.com OAuth — read-only Jetpack stats access per property.
  wpcomStatus: () =>
    client.get("/wordpress/wpcom/status").then((r) => r.data),
  wpcomAuthUrl: () =>
    client.get("/wordpress/wpcom/auth-url").then((r) => r.data),
  wpcomDisconnect: () =>
    client.delete("/wordpress/wpcom/connection").then((r) => r.data),

  // Social publishing (Facebook + Instagram via Meta Graph API).
  metaAuthUrl: () => client.get("/social/meta/auth-url").then((r) => r.data),
  listSocialConnections: () =>
    client.get("/social/connections").then((r) => r.data),
  metaLastAttempt: () =>
    client.get("/social/meta/last-attempt").then((r) => r.data),
  deleteSocialConnection: (pageId) =>
    client.delete(`/social/connections/${pageId}`).then((r) => r.data),
  setSocialConnectionVisibility: (pageId, hidden) =>
    client
      .post(`/social/connections/${pageId}/visibility`, { hidden })
      .then((r) => r.data),
  // Pin which Page the share panels pre-select on the active property.
  // No body — the page_id in the path is the whole payload.
  setSocialConnectionDefault: (pageId) =>
    client
      .post(`/social/connections/${pageId}/default`)
      .then((r) => r.data),
  shareToFacebook: (articleId, payload) =>
    client
      .post(`/articles/${articleId}/share/facebook`, payload)
      .then((r) => r.data),
  shareToInstagram: (articleId, payload) =>
    client
      .post(`/articles/${articleId}/share/instagram`, payload)
      .then((r) => r.data),
  shareToFacebookStory: (articleId, payload) =>
    client
      .post(`/articles/${articleId}/share/facebook-story`, payload)
      .then((r) => r.data),

  // Instagram carousel (routes/carousel.py)
  generateCarousel: (articleId, payload) =>
    client
      .post(`/articles/${articleId}/carousel/generate`, payload, { timeout: 180000 })
      .then((r) => r.data),
  saveCarousel: (articleId, payload) =>
    client.put(`/articles/${articleId}/carousel`, payload).then((r) => r.data),
  // Carousel photo library: upload / paste a file, or copy a source
  // photo or a Pexels/Unsplash photo into our storage. Both return
  // {image, carousel_images}.
  // purpose "panorama" keeps the photo wide (backend PANORAMA_IMAGE_WIDTH).
  uploadCarouselImage: async (articleId, file, { source = "upload", credit = "", purpose = "slide" } = {}) => {
    const processed = await preflightImage(file, {
      maxWidth: purpose === "panorama" ? PANORAMA_UPLOAD_WIDTH : MAX_UPLOAD_WIDTH,
    });
    const fd = new FormData();
    fd.append("file", processed);
    fd.append("source", source);
    fd.append("credit", credit || "");
    fd.append("purpose", purpose === "panorama" ? "panorama" : "slide");
    return client.post(`/articles/${articleId}/carousel/images/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    }).then((r) => r.data);
  },
  importCarouselImage: (articleId, { kind, url, credit, download_location }) =>
    client
      .post(`/articles/${articleId}/carousel/images/import`, {
        kind, url, credit: credit || "", download_location: download_location || "",
      }, { timeout: 60000 })
      .then((r) => r.data),
  // payload.slides: JPEG data URLs in slide order (2-10).
  shareInstagramCarousel: (articleId, payload) =>
    client
      .post(`/articles/${articleId}/share/instagram-carousel`, payload)
      .then((r) => r.data),

  // Scheduled social posts
  listScheduledPosts: (params = {}) =>
    client.get("/scheduled-posts", { params }).then((r) => r.data),
  cancelScheduledPost: (id) =>
    client.delete(`/scheduled-posts/${id}`).then((r) => r.data),

  // Delete a published Facebook/Instagram post from an article's social
  // badge: removes it on the platform AND every Newsdesk record of it.
  // `local_only` skips the platform call (Instagram / disconnected Page).
  deleteSocialPost: (articleId, { platform, post_id, local_only }) =>
    client
      .delete(`/articles/${articleId}/social-posts`, {
        params: { platform, post_id, local_only: !!local_only },
      })
      .then((r) => r.data),

  // Unified scheduled tasks (WordPress + Facebook + Instagram in one feed)
  listScheduledTasks: () =>
    client.get("/scheduled-tasks").then((r) => r.data),
  cancelScheduledTask: (kind, taskId) =>
    client
      .delete(`/scheduled-tasks/${kind}/${taskId}`)
      .then((r) => r.data),

  // Content Calendar — start/end are inclusive YYYY-MM-DD dates in the
  // property timezone.
  getContentCalendar: (start, end) =>
    client.get("/content-calendar", { params: { start, end } }).then((r) => r.data),

  // Analytics — which kinds of stories work (routes/analytics.py).
  getAnalyticsOverview: (days) =>
    client.get("/analytics/overview", { params: { days } }).then((r) => r.data),
  getAnalyticsStatus: () =>
    client.get("/analytics/status").then((r) => r.data),
  runAnalyticsSync: () =>
    client.post("/analytics/sync").then((r) => r.data),
  getAnalyticsDigest: () =>
    client.get("/analytics/digest").then((r) => r.data),
  generateAnalyticsDigest: (days) =>
    client.post("/analytics/digest", { days }).then((r) => r.data),

  // Social Comments workspace
  listSocialPosts: () =>
    client.get("/social/posts").then((r) => r.data),
  // `params` lets the Search tab pull a wider corpus (more posts per
  // source, higher row limit) than the Recent tab needs. Search then
  // filters that one cached payload client-side — no Graph call per
  // keystroke.
  listRecentSocialComments: (params) =>
    client.get("/social/recent-comments", { params }).then((r) => r.data),
  getSocialPostComments: (postId, params) =>
    client
      .get(`/social/posts/${postId}/comments`, { params })
      .then((r) => r.data),
  generateCommentReplies: (payload) =>
    client.post("/social/comments/generate", payload).then((r) => r.data),
  replyToSocialComment: (commentId, payload) =>
    client
      .post(`/social/comments/${commentId}/reply`, payload)
      .then((r) => r.data),

  // Prospects inbox (routes/prospects.py)
  prospectsSetup: () => client.get("/prospects/setup").then((r) => r.data),
  prospectsAuthUrl: (provider) =>
    client.get(`/prospects/${provider}/auth-url`).then((r) => r.data),
  prospectsUpdateConnection: (id, patch) =>
    client.put(`/prospects/connections/${id}`, patch).then((r) => r.data),
  prospectsDeleteConnection: (id) =>
    client.delete(`/prospects/connections/${id}`).then((r) => r.data),
  prospectsSyncNow: (id) =>
    client.post(`/prospects/connections/${id}/sync`, {}, { timeout: 600000 }).then((r) => r.data),
  prospectsSettings: () => client.get("/prospects/settings").then((r) => r.data),
  prospectsSaveSettings: (payload) =>
    client.put("/prospects/settings", payload).then((r) => r.data),
  prospectsCounts: (user) =>
    client.get("/prospects/counts", { params: user ? { user } : {} }).then((r) => r.data),
  prospectsThreads: (tab, user) =>
    client
      .get("/prospects/threads", { params: user ? { tab, user } : { tab } })
      .then((r) => r.data),
  prospectsThread: (id) => client.get(`/prospects/threads/${id}`).then((r) => r.data),
  prospectsDraft: (id, instruction) =>
    client.post(`/prospects/threads/${id}/draft`, { instruction }).then((r) => r.data),
  prospectsSend: (id, payload) =>
    client.post(`/prospects/threads/${id}/send`, payload).then((r) => r.data),
  prospectsMove: (id, tab, ignoreSender = false) =>
    client
      .post(`/prospects/threads/${id}/move`, { tab, ignore_sender: ignoreSender })
      .then((r) => r.data),
  prospectsSnooze: (id, days) =>
    client.post(`/prospects/threads/${id}/snooze`, { days }).then((r) => r.data),
  prospectsRetryPublishos: (id) =>
    client.post(`/prospects/threads/${id}/publishos`, {}).then((r) => r.data),
};
